import { useEffect, useState } from "react";
import { AlertTriangle, TrendingUp, ArrowRight, Zap } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ThreeQPageHeader — Composant générique doctrine 3Q pour n'importe quel module.
 *
 * V23.15.35 — Répond aux 3 questions en 1 bloc réutilisable :
 *  - Q1 « Et alors ? »     → narrative + chiffre choc
 *  - Q2 « Pourquoi ? »     → freshness/urgency indicator
 *  - Q3 « Que fais-je ? »  → CTA context-aware
 *
 * Usage :
 *   <ThreeQPageHeader
 *     endpoint="/workspace/analyses/top-priority"
 *     module="analyses"
 *     defaultCta={{ label: "Traiter maintenant", href: "/app/analyses" }}
 *   />
 *
 * L'endpoint doit renvoyer :
 *   { show, title, narrative, why_now, cta: { label, href }, impact_display?, color? }
 */
export default function ThreeQPageHeader({ endpoint, defaultColor = "#EF9F27", className = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(endpoint);
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint]);

  if (loading || !data || data.show === false) return null;

  const color = data.color || defaultColor;
  const Icon = data.severity === "critical" ? AlertTriangle : TrendingUp;

  return (
    <div
      className={`rounded-xl border p-3 sm:p-4 ${className}`}
      style={{
        background: `linear-gradient(135deg, ${color}10 0%, transparent 70%)`,
        borderColor: `${color}40`,
      }}
      data-testid="threeq-page-header"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${color}22`, border: `1px solid ${color}55` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
            {data.title || "Attention prioritaire"}
          </div>
          <div className="text-sm text-zinc-100 mt-1 leading-relaxed">
            {data.narrative}
            {data.impact_display && (
              <span className="font-semibold ml-1" style={{ color }}>· {data.impact_display}</span>
            )}
          </div>
          {data.why_now && (
            <div className="text-xs text-zinc-400 mt-1 italic">
              <Zap className="w-3 h-3 inline mr-1 text-[#FFB627]" />
              {data.why_now}
            </div>
          )}
          {data.cta && (
            <a
              href={data.cta.href}
              className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
              style={{ background: `${color}22`, border: `1px solid ${color}55`, color }}
              data-testid="threeq-page-cta"
            >
              {data.cta.label}
              <ArrowRight className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
